import PasswordInput from '../components/PasswordInput';
import { useState, useEffect, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import TermGradeReportCard from '../components/TermGradeReportCard';
import { ChildContext } from '../components/Layout/ParentLayout';
import {
  PlusIcon, XMarkIcon, PrinterIcon,
  TableCellsIcon, ListBulletIcon,
} from '@heroicons/react/24/outline';
import { createChatPage } from './ChatPage';

export const ParentMessages = createChatPage({ myRole: 'parent', contactLabel: 'teachers', accent: '#f97316' });

// ─── Shared helpers ────────────────────────────────────────────────────────────
const fmtDate      = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short'}) : '—';
const curMonth     = () => new Date().toISOString().slice(0,7);
const curYear      = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };
const pctColor     = (p) => p>=80?'#10c98e':p>=60?'#f59e0b':'#ef4444';
const pctBg        = (p) => p>=80?'#ecfdf5':p>=60?'#fffbeb':'#fef2f2';

// Shared style — same as student portal but orange accent
const sharedStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .pp * { box-sizing:border-box; }
  .pp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ppFade .4s ease both; }
  @keyframes ppFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .pp-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .pp-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .pp-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-orange{ background:#f97316; color:white; } .btn-orange:hover { background:#ea6c0a; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .pp-table { width:100%; border-collapse:collapse; }
  .pp-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .pp-table th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .pp-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .pp-table tbody tr:last-child { border-bottom:none; }
  .pp-table tbody tr:hover { background:#fafbff; }
  .pp-table td { padding:10px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .status-pill { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .status-pill.present  { background:#ecfdf5; color:#10c98e; }
  .status-pill.absent   { background:#fef2f2; color:#ef4444; }
  .status-pill.late     { background:#fffbeb; color:#f59e0b; }
  .status-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .status-pill.paid     { background:#ecfdf5; color:#10c98e; }
  .status-pill.unpaid   { background:#fef2f2; color:#ef4444; }
  .status-pill.overdue  { background:#fff7ed; color:#f97316; }
  .status-pill.pending  { background:#fffbeb; color:#f59e0b; }
  .status-pill.approved { background:#ecfdf5; color:#10c98e; }
  .status-pill.rejected { background:#fef2f2; color:#ef4444; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .pp-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .pp-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f97316; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .filter-row { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .filter-select,.filter-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; }
  .filter-select:focus,.filter-input:focus { border-color:#f97316; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:flex-start; justify-content:center; z-index:1000; padding:24px 16px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:460px; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; }
  .form-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-textarea:focus { border-color:#f97316; background:white; }
  .no-child { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:3rem; text-align:center; color:#9094a8; font-size:.82rem; }
  .pp-cards { display:none; padding:10px; }
  .pp-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .pp-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .pp-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.78rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .pp-card-row:last-of-type { border-bottom:none; }
  .pp-card-label { color:#9094a8; font-weight:600; flex-shrink:0; }
  @media (max-width:768px) {
    .pp-title{font-size:1.1rem;} .filter-row{flex-direction:column;}
    .pp-tbl-wrap { display:none !important; }
    .pp-cards { display:block; }
  }
  /* Results */
  .sr-cards { display:grid; grid-template-columns:repeat(auto-fill,minmax(190px,1fr)); gap:1rem; }
  .sr-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .sr-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.08); }
  .sr-card-icon { width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:.78rem; font-weight:700; margin-bottom:10px; }
  .sr-card-name { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .sr-card-meta { font-size:.7rem; color:#9094a8; margin-bottom:10px; }
  .sr-card-pct  { font-size:1.5rem; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1; }
  .sr-card-marks{ font-size:.7rem; color:#9094a8; margin-top:3px; white-space:nowrap; }
  .sr-card-bar  { height:5px; border-radius:99px; background:#f0f2f8; margin-top:8px; overflow:hidden; }
  .sr-card-fill { height:100%; border-radius:99px; }
  .sr-back { display:inline-flex; align-items:center; gap:5px; font-size:.78rem; font-weight:600; color:#6b7194; cursor:pointer; margin-bottom:1rem; }
  .sr-back:hover { color:#f97316; }
  .sr-overall { background:linear-gradient(135deg,#f97316,#ef4444); border-radius:16px; padding:1.1rem 1.4rem; color:white; margin-bottom:1.2rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; }
  .sr-overall-pct { font-size:2rem; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1; }
  .sr-assess-list { display:flex; flex-direction:column; gap:8px; }
  .sr-assess-item { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:12px 16px; display:flex; align-items:center; gap:12px; }
  .sr-assess-item:hover { border-color:#f97316; background:#fafbff; }
  .sr-assess-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:.7rem; font-weight:700; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
  .sr-badge { font-size:.7rem; font-weight:600; padding:4px 10px; border-radius:99px; }
  /* Timetable */
  .vtoggle { display:flex; gap:3px; background:#f0f2f7; border-radius:10px; padding:3px; }
  .vbtn { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; background:transparent; color:#9094a8; transition:all .15s; }
  .vbtn svg { width:13px; height:13px; stroke-width:2; }
  .vbtn.on { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
`;

// ── No child selected helper ───────────────────────────────────────────────────
const NoChild = () => (
  <div className="pp"><style>{sharedStyle}</style>
    <div className="no-child">
      <div style={{fontSize:'2rem',marginBottom:8}}>👶</div>
      <div style={{fontWeight:700,color:'#0f1123',marginBottom:4}}>Select a child</div>
      <div>Use the child tabs at the top to select which child to view.</div>
    </div>
  </div>
);

// ─── ATTENDANCE ────────────────────────────────────────────────────────────────
export const ParentAttendance = () => {
  const { activeChild } = useContext(ChildContext);
  const [view,      setView]      = useState('daily');
  const [month,     setMonth]     = useState(curMonth());
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [monthData, setMonthData] = useState(null);
  const [yearData,  setYearData]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [yLoading,  setYLoading]  = useState(false);
  const [leaves,    setLeaves]    = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ startDate:'', endDate:'', reason:'' });
  const [saving,    setSaving]    = useState(false);

  const MONTHS_ARR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const fetchMonthly = useCallback(async () => {
    if (!activeChild) return;
    setLoading(true);
    try {
      const [att, lv] = await Promise.all([
        api.get(`/parent/attendance/${activeChild._id}?month=${month}`),
        api.get(`/parent/leaves/${activeChild._id}`),
      ]);
      const [y,m] = month.split('-').map(Number);
      const today = new Date(); today.setHours(0,0,0,0);
      const end   = new Date(Math.min(today, new Date(y,m,0)));
      const map   = {}; (att.data?.records||[]).forEach(r=>{ map[new Date(r.date).toISOString().slice(0,10)]=r; });
      const all   = []; const cur = new Date(y,m-1,1);
      while (cur<=end) { const k=cur.toISOString().slice(0,10); all.push(map[k]||{ date:new Date(cur), status:'no-record', lateMinutes:0 }); cur.setDate(cur.getDate()+1); }
      setMonthData({ ...att.data, records:all });
      setLeaves(lv.data||[]);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [activeChild?._id, month]);

  const fetchYearly = async () => {
    if (!activeChild) return;
    setYLoading(true);
    try {
      const months = await Promise.all(
        Array.from({ length:12 }, (_,i) => {
          const m = String(i+1).padStart(2,'0');
          return api.get(`/parent/attendance/${activeChild._id}?month=${year}-${m}`)
            .then(r => ({ month:i+1, summary:r.data?.summary||{}, records:r.data?.records||[] }))
            .catch(() => ({ month:i+1, summary:{}, records:[] }));
        })
      );
      setYearData(months);
    } catch { toast.error('Failed to load yearly data'); }
    finally { setYLoading(false); }
  };

  useEffect(() => { if (view==='daily'||view==='monthly') fetchMonthly(); }, [fetchMonthly, view]);
  useEffect(() => { if (view==='yearly') fetchYearly(); }, [year, view, activeChild?._id]);

  const handleApplyLeave = async () => {
    if (!leaveForm.startDate||!leaveForm.endDate) return toast.error('Select dates');
    if (!leaveForm.reason.trim()) return toast.error('Reason is required');
    setSaving(true);
    try {
      const { data:l } = await api.post(`/parent/leaves/${activeChild._id}`, leaveForm);
      setLeaves(p=>[l,...p]); toast.success('Leave applied'); setLeaveOpen(false);
      setLeaveForm({ startDate:'', endDate:'', reason:'' });
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const handleCancel = async (id) => {
    try { await api.delete(`/parent/leaves/${activeChild._id}/${id}`); setLeaves(p=>p.filter(l=>l._id!==id)); toast.success('Cancelled'); }
    catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  // ── CSV download ──────────────────────────────────────────────────────────
  const downloadCSV = (records, filename) => {
    const rows = [['Date','Day','Status','Late Minutes']];
    records.forEach(r => {
      const d = new Date(r.date);
      rows.push([
        d.toLocaleDateString('en-GB'),
        d.toLocaleDateString('en-GB',{weekday:'long'}),
        r.status,
        r.lateMinutes||0,
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href=url; a.download=filename+'.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── XLSX download ─────────────────────────────────────────────────────────
  const downloadXLSX = (records, filename) => {
    // Build a simple XLSX-compatible CSV with BOM for Excel
    const rows = [['Date','Day','Status','Late Minutes']];
    records.forEach(r => {
      const d = new Date(r.date);
      rows.push([
        d.toLocaleDateString('en-GB'),
        d.toLocaleDateString('en-GB',{weekday:'long'}),
        r.status,
        r.lateMinutes||0,
      ]);
    });
    const csv = '\uFEFF' + rows.map(r => r.join('\t')).join('\n');
    const blob = new Blob([csv], { type:'application/vnd.ms-excel' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href=url; a.download=filename+'.xls'; a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeChild) return <NoChild/>;

  const { summary, records, attendancePct } = monthData||{};
  const monthLabel = new Date(month+'-01').toLocaleDateString('en-GB',{month:'long',year:'numeric'});

  const StatusPill = ({ r }) => {
    if (r.status==='holiday')   return <span className="status-pill" style={{background:'#f3f0ff',color:'#8b5cf6'}}>🎉 {r.note||'Holiday'}</span>;
    if (r.status==='off')       return <span className="status-pill" style={{background:'#f0f2f7',color:'#6b7194'}}>📅 Off Day</span>;
    if (r.status==='no-record') return <span style={{fontSize:'.67rem',color:'#c0c5dc',fontStyle:'italic'}}>—</span>;
    return <span className={`status-pill ${r.status}`}>{r.status}</span>;
  };

  const AttTable = ({ recs, filename }) => (
    <>
      <div className="pp-tbl-wrap" style={{overflowX:'auto'}}>
        <table className="pp-table">
          <thead><tr><th>Date</th><th>Day</th><th>Status</th><th>Late Min</th></tr></thead>
          <tbody>
            {recs.map((r,i)=>(
              <tr key={i}>
                <td><span className="mono">{fmtDateShort(r.date)}</span></td>
                <td><span style={{fontSize:'.74rem',color:'#9094a8'}}>{new Date(r.date).toLocaleDateString('en-GB',{weekday:'short'})}</span></td>
                <td><StatusPill r={r}/></td>
                <td><span className="mono">{r.lateMinutes>0?`${r.lateMinutes}m`:'—'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pp-cards">
        {recs.map((r,i)=>(
          <div key={i} className="pp-card">
            <div className="pp-card-top">
              <div>
                <div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{fmtDateShort(r.date)}</div>
                <div style={{fontSize:'.7rem',color:'#9094a8'}}>{new Date(r.date).toLocaleDateString('en-GB',{weekday:'short'})}</div>
              </div>
              <StatusPill r={r}/>
            </div>
            <div className="pp-card-row"><span className="pp-card-label">Late Min</span><span className="mono">{r.lateMinutes>0?`${r.lateMinutes}m`:'—'}</span></div>
          </div>
        ))}
      </div>
      {/* Download buttons */}
      <div style={{display:'flex',gap:8,padding:'12px 18px',borderTop:'1px solid #f0f2f8',flexWrap:'wrap'}}>
        <span style={{fontSize:'.74rem',color:'#9094a8',alignSelf:'center',flex:1}}>
          {recs.filter(r=>r.status!=='no-record').length} records
        </span>
        <button className="btn btn-ghost btn-sm" onClick={()=>downloadCSV(recs.filter(r=>r.status!=='no-record'),filename)}>
          ⬇ CSV
        </button>
        <button className="btn btn-ghost btn-sm" onClick={()=>downloadXLSX(recs.filter(r=>r.status!=='no-record'),filename)}>
          ⬇ Excel
        </button>
      </div>
    </>
  );

  const SummaryBar = ({ ms, pct, label }) => (
    <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1rem 1.4rem',marginBottom:'1rem',display:'flex',alignItems:'center',flexWrap:'wrap',gap:'.5rem'}}>
      <div style={{fontSize:'.9rem',fontWeight:700,color:'#0f1123',marginRight:'.8rem'}}>{label}</div>
      <div style={{display:'flex',flex:1,flexWrap:'wrap'}}>
        {[{lbl:'Present',val:ms.present||0,color:'#10c98e',bg:'#ecfdf5'},{lbl:'Absent',val:ms.absent||0,color:'#ef4444',bg:'#fef2f2'},{lbl:'Leave',val:ms.onLeave||0,color:'#3b6ef8',bg:'#eef2ff'},{lbl:'Late',val:ms.late||0,color:'#f59e0b',bg:'#fffbeb'}].map(({lbl,val,color,bg})=>(
          <div key={lbl} style={{flex:1,minWidth:68,textAlign:'center',padding:'0 .6rem',borderLeft:'1px solid #f0f2f7'}}>
            <div style={{fontSize:'.6rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:5}}>{lbl}</div>
            <div style={{width:30,height:30,borderRadius:'50%',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:'.88rem',fontWeight:700,background:bg,color}}>{val}</div>
          </div>
        ))}
        {pct!=null&&<div style={{flex:1,minWidth:68,textAlign:'center',padding:'0 .6rem',borderLeft:'1px solid #f0f2f7'}}>
          <div style={{fontSize:'.6rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:5}}>Pct</div>
          <div style={{width:30,height:30,borderRadius:'50%',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:'.78rem',fontWeight:700,background:pct>=90?'#ecfdf5':pct>=70?'#fffbeb':'#fef2f2',color:pct>=90?'#10c98e':pct>=70?'#f59e0b':'#ef4444'}}>{pct}%</div>
        </div>}
      </div>
    </div>
  );

  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr">
        <div>
          <div className="pp-title">Attendance — {activeChild.name}</div>
          <div className="pp-sub">{activeChild.class} · Section {activeChild.section}</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          {view==='daily'&&<input type="month" className="filter-input" value={month} onChange={e=>setMonth(e.target.value)}/>}
          {view==='monthly'&&<input type="month" className="filter-input" value={month} onChange={e=>setMonth(e.target.value)}/>}
          {view==='yearly'&&<input type="number" className="filter-input" value={year} min="2020" max="2099" onChange={e=>setYear(Number(e.target.value))} style={{width:100}}/>}
          {/* View tabs */}
          <div style={{display:'flex',gap:4,background:'#f0f2f7',borderRadius:10,padding:3}}>
            {['daily','monthly','yearly'].map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:'6px 14px',borderRadius:8,border:'none',fontFamily:"'Sora',sans-serif",fontSize:'.74rem',fontWeight:600,cursor:'pointer',background:view===v?'white':'transparent',color:view===v?'#0f1123':'#9094a8',boxShadow:view===v?'0 1px 4px rgba(0,0,0,.1)':'none',transition:'all .15s'}}>
                {v.charAt(0).toUpperCase()+v.slice(1)}
              </button>
            ))}
          </div>
          <button className="btn btn-orange" onClick={()=>setLeaveOpen(true)}><PlusIcon/>Apply Leave</button>
        </div>
      </div>

      {/* ── DAILY VIEW ── */}
      {view==='daily' && (
        loading ? <div className="pp-loading"><div className="spinner"/>Loading…</div> : <>
          {/* Today card */}
          {monthData && (() => {
            const todayKey = new Date().toISOString().slice(0,10);
            const todayRec = (monthData.records||[]).find(r => new Date(r.date).toISOString().slice(0,10)===todayKey);
            return (
              <div style={{background:todayRec?.status==='present'?'linear-gradient(135deg,#10c98e,#3b6ef8)':todayRec?.status==='absent'?'linear-gradient(135deg,#ef4444,#f97316)':'linear-gradient(135deg,#9094a8,#6b7194)',borderRadius:16,padding:'1.2rem 1.4rem',color:'white',marginBottom:'1.2rem',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'1rem'}}>
                <div>
                  <div style={{fontSize:'.76rem',fontWeight:600,opacity:.85,marginBottom:4}}>Today's Status — {activeChild.name}</div>
                  <div style={{fontSize:'1.1rem',fontWeight:700}}>
                    {todayRec?.status==='present'?'✓ Present':todayRec?.status==='absent'?'✗ Absent':todayRec?.status==='on-leave'?'◑ On Leave':todayRec?.status==='holiday'?'🎉 Holiday':todayRec?.status==='off'?'📅 Off Day':'No record yet'}
                  </div>
                  {(todayRec?.lateMinutes||0)>0&&<div style={{fontSize:'.74rem',marginTop:4,opacity:.9}}>⚠️ {todayRec.lateMinutes} minutes late</div>}
                </div>
                {summary&&<div style={{display:'flex',gap:'1.5rem',flexWrap:'wrap'}}>
                  {[{l:'Present',v:summary.present||0},{l:'Absent',v:summary.absent||0},{l:'Leave',v:summary.onLeave||0}].map(({l,v})=>(
                    <div key={l} style={{textAlign:'center'}}>
                      <div style={{fontSize:'.65rem',opacity:.7,fontWeight:600,textTransform:'uppercase'}}>{l}</div>
                      <div style={{fontSize:'1rem',fontWeight:700,marginTop:2}}>{v}</div>
                    </div>
                  ))}
                </div>}
              </div>
            );
          })()}
          <div className="panel">
            <div className="panel-head"><div className="panel-title">Today's Record</div></div>
            {(() => {
              const todayKey = new Date().toISOString().slice(0,10);
              const todayRec = (monthData?.records||[]).find(r=>new Date(r.date).toISOString().slice(0,10)===todayKey);
              return todayRec
                ? <AttTable recs={[todayRec]} filename={`attendance-today-${activeChild.name}`}/>
                : <div className="pp-empty">No attendance record for today yet.</div>;
            })()}
          </div>
        </>
      )}

      {/* ── MONTHLY VIEW ── */}
      {view==='monthly' && (
        loading ? <div className="pp-loading"><div className="spinner"/>Loading…</div> : <>
          {summary && <SummaryBar ms={summary} pct={attendancePct} label={monthLabel}/>}
          <div className="panel">
            <div className="panel-head"><div className="panel-title">Daily Records — {monthLabel}</div></div>
            {!records?.length
              ? <div className="pp-empty">No records this month.</div>
              : <AttTable recs={records} filename={`attendance-${activeChild.name}-${month}`}/>
            }
          </div>
        </>
      )}

      {/* ── YEARLY VIEW ── */}
      {view==='yearly' && (
        yLoading ? <div className="pp-loading"><div className="spinner"/>Loading yearly data…</div> : <>
          {/* Yearly summary cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:'1rem',marginBottom:'1rem'}}>
            {yearData.map(({month:m, summary:s, records:recs})=>{
              const present = s.present  || recs.filter(r=>r.status==='present').length;
              const absent  = s.absent   || recs.filter(r=>r.status==='absent').length;
              const onLeave = s.onLeave  || recs.filter(r=>r.status==='on-leave').length;
              const total   = present+absent+onLeave;
              const pct     = total>0?Math.round((present/total)*100):null;
              const bg  = pct===null?'#f0f2f7':pct>=90?'#ecfdf5':pct>=70?'#fffbeb':pct>0?'#fef2f2':'#f0f2f7';
              const col = pct===null?'#c0c5dc':pct>=90?'#10c98e':pct>=70?'#f59e0b':pct>0?'#ef4444':'#c0c5dc';
              return (
                <div key={m} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1rem 1.1rem',cursor:'pointer',transition:'all .15s'}}
                  onClick={()=>{ setMonth(`${year}-${String(m).padStart(2,'0')}`); setView('monthly'); }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='#f97316';e.currentTarget.style.transform='translateY(-1px)';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='#e8ebf4';e.currentTarget.style.transform='none';}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                    <div style={{fontWeight:700,fontSize:'.9rem',color:'#0f1123'}}>{MONTHS_ARR[m-1]} {year}</div>
                    <div style={{fontWeight:700,fontSize:'1rem',fontFamily:"'JetBrains Mono',monospace",color:col}}>{pct!=null?`${pct}%`:'—'}</div>
                  </div>
                  <div style={{height:6,borderRadius:99,background:'#f0f2f8',overflow:'hidden',marginBottom:8}}>
                    <div style={{height:'100%',borderRadius:99,width:`${pct??0}%`,background:col,transition:'width .5s'}}/>
                  </div>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap',fontSize:'.72rem',fontWeight:600}}>
                    <span style={{color:'#10c98e'}}>✓ {present}</span>
                    <span style={{color:'#ef4444'}}>✗ {absent}</span>
                    <span style={{color:'#3b6ef8'}}>◑ {onLeave}</span>
                  </div>
                  <div style={{fontSize:'.68rem',color:'#9094a8',marginTop:6}}>Tap to view details →</div>
                </div>
              );
            })}
          </div>

          {/* Download full year */}
          {yearData.length > 0 && (() => {
            const allRecs = yearData.flatMap(({month:m,records:recs}) =>
              recs.filter(r=>r.status!=='no-record').map(r=>({...r, _monthName:MONTHS_ARR[m-1]}))
            );
            if (!allRecs.length) return null;
            return (
              <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'12px 18px',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                <span style={{fontSize:'.78rem',color:'#6b7194',flex:1}}>Download full {year} attendance report</span>
                <button className="btn btn-ghost btn-sm" onClick={()=>downloadCSV(allRecs,`attendance-${activeChild.name}-${year}`)}>⬇ CSV</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>downloadXLSX(allRecs,`attendance-${activeChild.name}-${year}`)}>⬇ Excel</button>
              </div>
            );
          })()}
        </>
      )}

      {/* Leave applications */}
      {leaves.length>0&&(
        <div className="panel" style={{marginTop:'1.2rem'}}>
          <div className="panel-head"><div className="panel-title">Leave Applications ({leaves.length})</div></div>
          <div className="pp-tbl-wrap" style={{overflowX:'auto'}}>
            <table className="pp-table">
              <thead><tr><th>Dates</th><th>Reason</th><th>Status</th><th>Note</th><th></th></tr></thead>
              <tbody>
                {leaves.map(l=>(
                  <tr key={l._id}>
                    <td><span className="mono">{fmtDateShort(l.startDate)}{l.startDate!==l.endDate?` → ${fmtDateShort(l.endDate)}`:''}</span></td>
                    <td style={{fontSize:'.76rem',color:'#6b7194',maxWidth:160}}>{l.reason||'—'}</td>
                    <td><span className={`status-pill ${l.status}`}>{l.status}</span></td>
                    <td style={{fontSize:'.72rem',color:'#6b7194',fontStyle:'italic'}}>{l.adminNote||'—'}</td>
                    <td>{l.status==='pending'&&<button className="btn btn-red btn-sm" onClick={()=>handleCancel(l._id)}>Cancel</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pp-cards">
            {leaves.map(l=>(
              <div key={l._id} className="pp-card">
                <div className="pp-card-top">
                  <span className="mono">{fmtDateShort(l.startDate)}{l.startDate!==l.endDate?` → ${fmtDateShort(l.endDate)}`:''}</span>
                  <span className={`status-pill ${l.status}`}>{l.status}</span>
                </div>
                <div className="pp-card-row"><span className="pp-card-label">Reason</span><span style={{textAlign:'right'}}>{l.reason||'—'}</span></div>
                {l.adminNote && <div className="pp-card-row"><span className="pp-card-label">Note</span><span style={{textAlign:'right',fontStyle:'italic'}}>{l.adminNote}</span></div>}
                {l.status==='pending' && <div style={{marginTop:8}}><button className="btn btn-red btn-sm" onClick={()=>handleCancel(l._id)}>Cancel</button></div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {leaveOpen&&createPortal(
        <div className="modal-backdrop" onClick={()=>setLeaveOpen(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Apply Leave for {activeChild.name}</div><button className="modal-close" onClick={()=>setLeaveOpen(false)}><XMarkIcon/></button></div>
            <div className="form-field"><label className="form-label">Start Date</label><input type="date" className="form-input" value={leaveForm.startDate} onChange={e=>setLeaveForm(p=>({...p,startDate:e.target.value}))}/></div>
            <div className="form-field"><label className="form-label">End Date</label><input type="date" className="form-input" value={leaveForm.endDate} onChange={e=>setLeaveForm(p=>({...p,endDate:e.target.value}))}/></div>
            <div className="form-field"><label className="form-label">Reason</label><textarea className="form-input" rows={3} required value={leaveForm.reason} onChange={e=>setLeaveForm(p=>({...p,reason:e.target.value}))}/></div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setLeaveOpen(false)}>Cancel</button>
              <button className="btn btn-orange" onClick={handleApplyLeave} disabled={saving}>{saving?'Saving…':'Apply Leave'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};


// ─── RESULTS ──────────────────────────────────────────────────────────────────
export const ParentResults = () => {
  const { activeChild }  = useContext(ChildContext);
  const [year,          setYear]         = useState(curYear());
  const [data,          setData]         = useState(null);
  const [loading,       setLoading]      = useState(false);
  const [activeSubject, setActiveSubject]= useState(null);

  const SUBJ_COLORS = ['#3b6ef8','#10c98e','#f97316','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899'];

  const fetchData = useCallback(async () => {
    if (!activeChild) return;
    setLoading(true);
    try { const {data:r} = await api.get(`/parent/results/${activeChild._id}?academicYear=${year}`); setData(r); setActiveSubject(null); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, [activeChild?._id, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!activeChild) return <NoChild/>;

  const subjectData = activeSubject ? data?.subjectResults?.find(s=>s.subject===activeSubject) : null;

  if (activeSubject && subjectData) {
    const colorIdx = data.subjectResults.findIndex(s=>s.subject===activeSubject);
    const color    = SUBJ_COLORS[colorIdx%SUBJ_COLORS.length];
    const isTerm   = data.mode === 'term';
    return (
      <div className="pp"><style>{sharedStyle}</style>
        <div className="pp-hdr">
          <div>
            <div className="sr-back" onClick={()=>setActiveSubject(null)}>← All Subjects</div>
            <div className="pp-title">{activeSubject}</div>
            <div className="pp-sub">{`${year} · ${subjectData.entries?.length||0} assessment${subjectData.entries?.length!==1?'s':''}`}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={()=>window.print()}><PrinterIcon/>Print</button>
        </div>
        <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.3rem',marginBottom:'1rem',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
          <div>
            <div style={{fontSize:'1rem',fontWeight:700,color:'#0f1123'}}>{activeSubject}</div>
            {!isTerm && <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>{subjectData.totalObtained} / {subjectData.totalMax} marks</div>}
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {subjectData.percentage!=null&&<span className="sr-badge" style={{background:pctBg(subjectData.percentage),color:pctColor(subjectData.percentage)}}>{isTerm?'Overall':'You'}: {subjectData.percentage}%</span>}
          </div>
        </div>
        <div className="sr-assess-list">
          {(subjectData.entries||[]).slice().sort((a,b)=>new Date(a.date||0)-new Date(b.date||0)).map((e,i)=>{
            const online=e.source==='online'; const pending=!!e.pending;
            const obtained=(e.isAbsent||e.isNA||pending)?null:e.marksObtained;
            const pct=obtained!=null&&e.maxMarks>0?Math.round((obtained/e.maxMarks)*100):null;
            return (
              <div key={`${e.source||'a'}-${e.assessmentId||''}-${i}`} className="sr-assess-item">
                <div className="sr-assess-icon" style={{background:`${color}18`,color}}>{(e.typeName||'').slice(0,3).toUpperCase()}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:'.84rem',fontWeight:700,color:'#0f1123'}}>
                    {e.typeName}
                    {online&&<span className="sr-badge" style={{background:'#eef2ff',color:'#3b6ef8',marginLeft:6}}>{pending?'Online · awaiting final marks':'Online'}</span>}
                  </div>
                  {e.description&&<div style={{fontSize:'.72rem',color:'#6b7194',marginTop:1}}>{e.description}</div>}
                  {e.remarks&&<div style={{fontSize:'.7rem',color:'#9094a8',marginTop:1,fontStyle:'italic'}}>{e.remarks}</div>}
                  <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:2}}>📅 {fmtDate(e.date)}</div>
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  {pending?<div style={{fontSize:'.72rem',fontWeight:700,color:'#f59e0b'}}>Pending</div>
                  :e.isAbsent?<div style={{fontSize:'.82rem',fontWeight:700,color:'#ef4444'}}>Absent</div>
                  :e.isNA?<div style={{fontSize:'.82rem',fontWeight:700,color:'#9094a8'}}>N/A</div>
                  :<>
                    <div style={{whiteSpace:'nowrap'}}>
                      <span style={{fontSize:'1.05rem',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:pct!=null?pctColor(pct):'#b0b5cc'}}>{obtained??'—'}</span>
                      <span style={{fontSize:'.68rem',color:'#9094a8'}}>/{e.maxMarks}</span>
                    </div>
                    {pct!=null&&<span className="sr-badge" style={{background:pctBg(pct),color:pctColor(pct),marginTop:3,display:'inline-block'}}>{pct}%</span>}
                  </>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const isTermMode = data?.mode === 'term';
  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr">
        <div><div className="pp-title">Results — {activeChild.name}</div><div className="pp-sub">Tap a subject to see detailed results</div></div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <input type="text" className="filter-input" value={year} onChange={e=>setYear(e.target.value)} placeholder="2025-2026" style={{width:130}}/>
          {data&&<button className="btn btn-ghost btn-sm" onClick={()=>window.print()}><PrinterIcon/>Print</button>}
        </div>
      </div>
      {loading?<div className="pp-loading"><div className="spinner"/>Loading…</div>
      :!data?.subjectResults?.length?<div className="pp-empty">No results for {year}.</div>
      :(
        <>
          <div className="sr-overall">
            <div>
              <div style={{fontSize:'.76rem',opacity:.8,marginBottom:3}}>Overall — {year}</div>
              <div className="sr-overall-pct">{data.overallPercentage??'—'}{data.overallPercentage!=null?'%':''}</div>
              {!isTermMode && <div style={{fontSize:'.74rem',opacity:.7,marginTop:3}}>{data.grandTotalObtained} / {data.grandTotalMax} marks</div>}
            </div>
            <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
              <div style={{textAlign:'center'}}><div style={{fontSize:'1.1rem',fontWeight:700}}>{data.subjectResults.length}</div><div style={{fontSize:'.62rem',opacity:.7,textTransform:'uppercase'}}>Subjects</div></div>
              <div style={{textAlign:'center'}}><div style={{fontSize:'1.1rem',fontWeight:700}}>{data.subjectResults.filter(s=>(s.percentage??0)>=60).length}</div><div style={{fontSize:'.62rem',opacity:.7,textTransform:'uppercase'}}>Passed</div></div>
              <div style={{textAlign:'center'}}><div style={{fontSize:'1.1rem',fontWeight:700}}>{data.subjectResults.reduce((s,r)=>s+(r.entries?.length||0),0)}</div><div style={{fontSize:'.62rem',opacity:.7,textTransform:'uppercase'}}>Tests</div></div>
            </div>
          </div>
          <div className="sr-cards">
            {data.subjectResults.map((subj,i)=>{
              const color=SUBJ_COLORS[i%SUBJ_COLORS.length]; const pct=subj.percentage;
              return (
                <div key={subj.subject} className="sr-card" style={{borderTop:`3px solid ${color}`}} onClick={()=>setActiveSubject(subj.subject)}>
                  <div className="sr-card-icon" style={{background:`${color}18`,color}}>{subj.subject.slice(0,3).toUpperCase()}</div>
                  <div className="sr-card-name">{subj.subject}</div>
                  <div className="sr-card-meta">{`${subj.entries?.length||0} test${subj.entries?.length!==1?'s':''} · tap to view`}</div>
                  <div className="sr-card-pct" style={{color:pct!=null?pctColor(pct):'#c0c5dc'}}>{pct??'—'}{pct!=null?'%':''}</div>
                  {!isTermMode && <div className="sr-card-marks">{subj.totalObtained} / {subj.totalMax} marks</div>}
                  <div className="sr-card-bar"><div className="sr-card-fill" style={{width:`${pct??0}%`,background:pct!=null?pctColor(pct):'#e0e4f0'}}/></div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ─── GRADE REPORT (Term-Based) ───────────────────────────────────────────────
export const ParentGradeReport = () => {
  const { activeChild } = useContext(ChildContext);
  if (!activeChild) return <NoChild/>;
  return <div className="pp"><TermGradeReportCard endpoint={`/parent/grade-report/${activeChild._id}`} title={`Grade Report — ${activeChild.name}`} resultsPath="/parent/results" accent="#f97316"/></div>;
};

// ─── FEES ─────────────────────────────────────────────────────────────────────
export const ParentFees = () => {
  const { activeChild } = useContext(ChildContext);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeChild) return;
    setLoading(true);
    api.get(`/parent/fees/${activeChild._id}`)
      .then(r=>setData(r.data)).catch(()=>toast.error('Failed'))
      .finally(()=>setLoading(false));
  }, [activeChild?._id]);

  if (!activeChild) return <NoChild/>;
  const fmt = (n) => `Rs. ${Number(n||0).toLocaleString('en-PK')}`;
  const fmtHostel = (n) => `Rs. ${Number(n||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const statusColor={paid:'#10c98e',unpaid:'#ef4444',overdue:'#ea580c',waived:'#6b7280',pending_verification:'#9333ea'};
  const statusBg={paid:'#ecfdf5',unpaid:'#fef2f2',overdue:'#fff7ed',waived:'#f3f4f6',pending_verification:'#fdf4ff'};
  const statusLabel={paid:'✓ Paid',unpaid:'• Unpaid',overdue:'⚠ Overdue',waived:'Waived',pending_verification:'📎 Proof Submitted'};

  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr">
        <div><div className="pp-title">Fees — {activeChild.name}</div><div className="pp-sub">View monthly fee bills</div></div>
      </div>
      {loading?<div className="pp-loading"><div className="spinner"/>Loading…</div>
      :!data?null:(
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'.8rem',marginBottom:'1.4rem'}}>
            <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'.9rem 1rem'}}>
              <div style={{fontSize:'.66rem',fontWeight:600,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Total Paid</div>
              <div style={{fontSize:'1.1rem',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:'#10c98e'}}>{fmt(data.summary?.paid)}</div>
            </div>
            <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'.9rem 1rem'}}>
              <div style={{fontSize:'.66rem',fontWeight:600,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Pending</div>
              <div style={{fontSize:'1.1rem',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:data.summary?.pending>0?'#ef4444':'#10c98e'}}>{fmt(data.summary?.pending)}</div>
            </div>
            <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'.9rem 1rem'}}>
              <div style={{fontSize:'.66rem',fontWeight:600,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Overdue</div>
              <div style={{fontSize:'1.1rem',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:data.summary?.overdue>0?'#ea580c':'#10c98e'}}>{data.summary?.overdue} bill{data.summary?.overdue!==1?'s':''}</div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-head"><div className="panel-title">Fee Bills ({data.records?.length})</div></div>
            {!data.records?.length?<div className="pp-empty">No fee bills yet.</div>
            :data.records.map(rec=>{
              return (
                <div key={rec._id} style={{padding:'14px 18px',borderBottom:'1px solid #f3f5fb'}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                    <div>
                      <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.84rem',fontWeight:700,color:'#0f1123'}}>{rec.month}</div>
                      <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>{rec.class?.name}{rec.dueDate&&` · Due: ${fmtDate(rec.dueDate)}`}</div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.9rem',fontWeight:700,color:'#0f1123'}}>{fmt(rec.totalDue)}</div>
                        {rec.status==='paid'&&<div style={{fontSize:'.68rem',color:'#9094a8'}}>Paid {fmtDate(rec.paidAt)}</div>}
                      </div>
                      <span style={{fontSize:'.68rem',fontWeight:600,padding:'3px 10px',borderRadius:99,background:statusBg[rec.status],color:statusColor[rec.status],whiteSpace:'nowrap'}}>{statusLabel[rec.status]}</span>
                    </div>
                  </div>
                  {(
                    <div style={{marginTop:8,padding:'8px 12px',background:'#f8f9fc',borderRadius:8,fontSize:'.76rem'}}>
                      <div style={{display:'flex',justifyContent:'space-between',color:'#6b7194',marginBottom:3}}><span>Base Fee</span><span style={{fontFamily:'monospace'}}>{fmt(rec.amount)}</span></div>
                      <div style={{display:'flex',justifyContent:'space-between',color:'#6b7194',marginBottom:3}}><span>Hostel Fee</span><span style={{fontFamily:'monospace'}}>{fmtHostel(rec.hostelFee)}</span></div>
                      {(rec.fines||[]).map(f=><div key={f._id} style={{display:'flex',justifyContent:'space-between',marginBottom:2,color:'#ea580c'}}><span>Fine: {f.reason}</span><span style={{fontFamily:'monospace'}}>+ {fmt(f.amount)}</span></div>)}
                      {rec.discount>0&&<div style={{display:'flex',justifyContent:'space-between',color:'#10c98e'}}><span>Discount</span><span style={{fontFamily:'monospace'}}>− {fmt(rec.discount)}</span></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ─── MATERIALS ────────────────────────────────────────────────────────────────
export const ParentMaterials = () => {
  const { activeChild } = useContext(ChildContext);
  const [materials,     setMaterials]     = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [navStep,       setNavStep]       = useState('subjects');
  const [activeSubject, setActiveSubject] = useState('');
  const [filterType,    setFilterType]    = useState('');

  const SUBJ_COLORS=['#3b6ef8','#10c98e','#f97316','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899'];
  const TYPE_ICON={pdf:'📄',doc:'📝',ppt:'📊',image:'🖼️',video:'🎬',youtube:'▶️',other:'📎'};
  const TYPE_COLOR={pdf:'#ef4444',doc:'#3b6ef8',ppt:'#f59e0b',image:'#10c98e',video:'#8b5cf6',youtube:'#ef4444',other:'#6b7194'};

  useEffect(() => {
    if (!activeChild) return;
    setLoading(true);
    api.get(`/parent/materials/${activeChild._id}`)
      .then(r=>setMaterials(r.data||[])).catch(()=>toast.error('Failed'))
      .finally(()=>setLoading(false));
  }, [activeChild?._id]);

  if (!activeChild) return <NoChild/>;

  const grouped  = materials.reduce((acc,m)=>{ const k=m.subject||'General'; if(!acc[k])acc[k]=[]; acc[k].push(m); return acc; },{});
  const subjects = Object.keys(grouped);
  const subjectMaterials = activeSubject ? (grouped[activeSubject]||[]).filter(m=>!filterType||m.type===filterType) : [];

  if (navStep==='list') return (
    <div className="pp"><style>{sharedStyle}</style>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:'1.2rem',flexWrap:'wrap'}}>
        <span style={{fontSize:'.78rem',fontWeight:600,color:'#9094a8',cursor:'pointer'}} onClick={()=>{setNavStep('subjects');setActiveSubject('');setFilterType('');}}>Materials</span>
        <span style={{color:'#d1d5db'}}>›</span>
        <span style={{fontSize:'.78rem',fontWeight:600,color:'#0f1123'}}>{activeSubject}</span>
      </div>
      <div className="pp-hdr"><div><div className="pp-title">{activeSubject}</div><div className="pp-sub">{subjectMaterials.length} material{subjectMaterials.length!==1?'s':''}</div></div></div>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:'1rem'}}>
        {['','doc','image','other','pdf','ppt','video','youtube'].map(t=>(
          <button key={t} onClick={()=>setFilterType(t)} style={{padding:'5px 12px',borderRadius:8,border:'1.5px solid',fontFamily:"'Sora',sans-serif",fontSize:'.74rem',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',borderColor:filterType===t?'#f97316':'#e0e4f0',background:filterType===t?'#f97316':'white',color:filterType===t?'white':'#6b7194'}}>
            {t?(TYPE_ICON[t]||'📎')+' '+t.toUpperCase():'All'}
          </button>
        ))}
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Materials ({subjectMaterials.length})</div></div>
        {!subjectMaterials.length?<div className="pp-empty">No materials found.</div>
        :<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10,padding:10}}>
          {subjectMaterials.map(m=>(
            <div key={m._id} style={{borderRadius:12,border:'1px solid #edf0f9',padding:'12px 14px',background:'#fafbff',display:'flex',flexDirection:'column',gap:6,transition:'all .15s'}} onMouseEnter={e=>{e.currentTarget.style.borderColor='#f97316';}} onMouseLeave={e=>{e.currentTarget.style.borderColor='#edf0f9';}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:9}}>
                <div style={{width:30,height:30,borderRadius:8,background:`${TYPE_COLOR[m.type]||'#6b7194'}18`,color:TYPE_COLOR[m.type]||'#6b7194',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.82rem',flexShrink:0}}>{TYPE_ICON[m.type]||'📎'}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:'.82rem',fontWeight:600,color:'#0f1123',lineHeight:1.35}}>{m.title}</div>
                  <div style={{fontSize:'.68rem',color:'#9094a8'}}>{m.uploadedBy?.name&&`${m.uploadedBy.name} · `}{m.createdAt?new Date(m.createdAt).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):''}</div>
                </div>
              </div>
              {m.description&&<div style={{fontSize:'.72rem',color:'#6b7194',lineHeight:1.45}}>{m.description}</div>}
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:2}}>
                {(m.fileUrl||m.youtubeUrl)&&<a href={m.fileUrl||m.youtubeUrl} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:'.72rem',fontWeight:600,padding:'4px 10px',borderRadius:7,border:'1.5px solid #f97316',color:'#f97316',background:'white',textDecoration:'none'}}>{m.type==='youtube'?'▶ Watch':m.type==='video'?'▶ Play':m.type==='image'?'🔍 View':'⬇ Open'}</a>}
              </div>
            </div>
          ))}
        </div>}
      </div>
    </div>
  );

  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr"><div><div className="pp-title">Materials — {activeChild.name}</div><div className="pp-sub">Study materials by subject</div></div></div>
      {loading?<div className="pp-loading"><div className="spinner"/>Loading…</div>
      :!subjects.length?<div className="pp-empty">No materials yet.</div>
      :<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:'1rem'}}>
        {subjects.map((subj,i)=>{
          const color=SUBJ_COLORS[i%SUBJ_COLORS.length]; const count=grouped[subj].length;
          const breakdown=grouped[subj].reduce((acc,m)=>{ acc[m.type]=(acc[m.type]||0)+1; return acc; },{});
          return (
            <div key={subj} onClick={()=>{ setActiveSubject(subj); setNavStep('list'); setFilterType(''); }} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.2rem',cursor:'pointer',transition:'all .15s'}} onMouseEnter={e=>{e.currentTarget.style.borderColor=color;e.currentTarget.style.transform='translateY(-1px)';}} onMouseLeave={e=>{e.currentTarget.style.borderColor='#e8ebf4';e.currentTarget.style.transform='none';}}>
              <div style={{width:36,height:36,borderRadius:10,background:`${color}18`,color,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',marginBottom:8}}>{subj.slice(0,3).toUpperCase()}</div>
              <div style={{fontSize:'.92rem',fontWeight:700,color:'#0f1123',marginBottom:3}}>{subj}</div>
              <div style={{fontSize:'.72rem',color:'#9094a8'}}>{count} material{count!==1?'s':''}</div>
              <div style={{display:'flex',gap:4,flexWrap:'wrap',marginTop:6}}>
                {Object.entries(breakdown).slice(0,3).map(([t,c])=>(
                  <span key={t} style={{fontSize:'.63rem',fontWeight:600,padding:'2px 6px',borderRadius:99,background:`${TYPE_COLOR[t]||'#6b7194'}14`,color:TYPE_COLOR[t]||'#6b7194'}}>{TYPE_ICON[t]} {c}</span>
                ))}
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:10}}>
                <span style={{fontSize:'.72rem',color,fontWeight:600}}>View materials</span>
                <span style={{color,fontSize:'.9rem'}}>›</span>
              </div>
            </div>
          );
        })}
      </div>}
    </div>
  );
};

// ─── NOTICES ─────────────────────────────────────────────────────────────────
export const ParentNotices = () => {
  const { activeChild } = useContext(ChildContext);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeChild) return;
    setLoading(true);
    api.get(`/parent/notices/${activeChild._id}`)
      .then(r=>setNotices(r.data||[])).catch(()=>toast.error('Failed'))
      .finally(()=>setLoading(false));
  }, [activeChild?._id]);

  if (!activeChild) return <NoChild/>;
  const roleColor=(role)=>role==='admin'?'#3b6ef8':role==='teacher'?'#10c98e':'#6b7194';

  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr"><div><div className="pp-title">Notices — {activeChild.name}</div><div className="pp-sub">Announcements from school</div></div></div>
      {loading?<div className="pp-loading"><div className="spinner"/>Loading…</div>
      :!notices.length?<div className="pp-empty">No notices yet.</div>
      :<div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
        {notices.map(n=>(
          <div key={n._id} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.3rem'}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:8,flexWrap:'wrap'}}>
              <div style={{fontWeight:700,fontSize:'.9rem',color:'#0f1123',flex:1}}>{n.title}</div>
              <span style={{fontSize:'.67rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:`${roleColor(n.postedBy?.role)}18`,color:roleColor(n.postedBy?.role),whiteSpace:'nowrap',flexShrink:0}}>{n.postedBy?.role}</span>
            </div>
            <div style={{fontSize:'.82rem',color:'#4a5075',lineHeight:1.6,marginBottom:10}}>{n.content}</div>
            <div style={{fontSize:'.7rem',color:'#b0b5cc'}}>{n.postedBy?.name} · {fmtDate(n.createdAt)}</div>
          </div>
        ))}
      </div>}
    </div>
  );
};

// ─── TIMETABLE ────────────────────────────────────────────────────────────────
// Reuses the same timetable logic — import StudentTimetable component adapted for parent
export const ParentTimetable = () => {
  const { activeChild } = useContext(ChildContext);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [activeDay, setActiveDay] = useState('All');
  const [viewMode,  setViewMode]  = useState('list');

  const ALL_DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const COLORS   = ['#3b6ef8','#10c98e','#f97316','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899'];
  const DAY_DOW  = { Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6, Sunday:0 };
  const subjColor = (s, arr) => COLORS[arr.indexOf(s)>=0?arr.indexOf(s)%COLORS.length:0];

  useEffect(() => {
    if (!activeChild) return;
    setLoading(true);
    api.get(`/parent/timetable/${activeChild._id}`)
      .then(r=>{ setData(r.data); setViewMode(r.data?.timetableView||'list'); })
      .catch(()=>toast.error('Failed'))
      .finally(()=>setLoading(false));
  }, [activeChild?._id]);

  if (!activeChild) return <NoChild/>;
  if (loading) return <div className="pp"><style>{sharedStyle}</style><div className="pp-loading"><div className="spinner"/>Loading timetable…</div></div>;

  const entries    = data?.timetable||[];
  const lunchStart = data?.lunchBreakStart||'';
  const lunchEnd   = data?.lunchBreakEnd||'';
  const weeklyOff  = data?.weeklyOffDays||[0];
  const satPolicy  = data?.saturdayPolicy||'all_off';
  const holidays   = data?.holidays||[];

  const isOffDay = (d) => {
    if (d==='Sunday') return true;
    if (d==='Saturday') return satPolicy==='all_off';
    return (weeklyOff||[]).filter(x=>x!==6).includes(DAY_DOW[d]);
  };
  const satNote = (d) => d!=='Saturday'?null:satPolicy==='alternate_odd_off'?'1st & 3rd off':satPolicy==='alternate_even_off'?'2nd & 4th off':null;

  const holidaysByDay = {};
  holidays.forEach(h=>{ const dow=new Date(h.date).getDay(); const day=ALL_DAYS.find(d=>DAY_DOW[d]===dow); if(day){if(!holidaysByDay[day])holidaysByDay[day]=[];holidaysByDay[day].push(h);} });
  const getDayType = (d) => d==='Sunday'?'sunday':isOffDay(d)?'off':holidaysByDay[d]?.length?'holiday':'normal';

  const uniqSubjs  = [...new Set(entries.map(e=>e.subject).filter(Boolean))];
  const allPeriods = [...new Set(entries.map(e=>e.period))].sort((a,b)=>a-b);
  const gridMap    = {};
  entries.forEach(e=>{ if(!gridMap[e.period])gridMap[e.period]={}; if(!gridMap[e.period][e.day])gridMap[e.period][e.day]=[]; gridMap[e.period][e.day].push(e); });

  const lunchAfterIdx = () => {
    if (!lunchStart||!allPeriods.length) return -1;
    const [lh,lm]=lunchStart.split(':').map(Number);
    for (let i=0;i<allPeriods.length-1;i++) {
      const cur=entries.find(e=>e.period===allPeriods[i]&&e.endTime); const nxt=entries.find(e=>e.period===allPeriods[i+1]&&e.startTime);
      if(cur&&nxt){const [ch,cm]=cur.endTime.split(':').map(Number);const [nh,nm]=nxt.startTime.split(':').map(Number);if(ch*60+cm<=lh*60+lm&&lh*60+lm<=nh*60+nm)return i;}
    }
    return -1;
  };
  const lunchAfter = lunchAfterIdx();
  const cols=[]; allPeriods.forEach((p,i)=>{ cols.push({t:'p',p}); if(i===lunchAfter)cols.push({t:'l'}); });
  const periodTime=(p)=>{ const e=entries.find(x=>x.period===p&&x.startTime); if(!e)return null; return e.endTime?`${e.startTime}–${e.endTime}`:e.startTime; };

  const listDays = activeDay==='All'?ALL_DAYS:[activeDay];

  const ttExtraStyle = `
    .dtabs{display:flex;gap:4px;margin-bottom:1.2rem;overflow-x:auto;padding-bottom:4px;scrollbar-width:none;}
    .dtabs::-webkit-scrollbar{display:none;}
    .dtab{padding:5px 14px;border-radius:8px;border:1.5px solid #e0e4f0;background:white;font-family:'Sora',sans-serif;font-size:.74rem;font-weight:600;cursor:pointer;color:#9094a8;white-space:nowrap;flex-shrink:0;transition:all .15s;}
    .dtab.on{background:#fff7ed;border-color:#f97316;color:#f97316;}
    .dtab.off-tab{border-color:#fecaca;color:#ef4444;background:#fef2f2;}
    .dtab.off-tab.on{background:#ef4444;color:white;}
    .dtab.hol-tab{border-color:#a7f3d0;color:#10c98e;background:#ecfdf5;}
    .gwrap{overflow-x:auto;-webkit-overflow-scrolling:touch;width:100%;}
    .gt{border-collapse:collapse;min-width:600px;table-layout:fixed;}
    .gt th{padding:10px 12px;font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;background:#fafbff;border:1px solid #edf0f9;text-align:center !important;white-space:nowrap;}
    .gt th.dh{background:#f0f2f7;text-align:left;padding-left:14px;width:100px;min-width:90px;color:#6b7194;}
    .gt th.lh{background:#fffbeb;color:#92400e;min-width:80px;}
    .gt td{ border:1px solid #edf0f9;padding:6px 8px;vertical-align:top;width:130px;min-width:110px;height:72px; text-align:center !important; }
    .gt td.dc{background:#f8f9fc;font-weight:700;font-size:.82rem;color:#0f1123;white-space:nowrap;vertical-align:middle;padding:10px 14px;border-right:2px solid #e8ebf4;}
    .gt td.off-dc{background:#fef9f9;color:#ef4444;font-weight:700;font-size:.82rem;vertical-align:middle;padding:10px 14px;border-right:2px solid #e8ebf4;}
    .gt td.sun-dc{background:#f8f9fc;color:#9094a8;font-weight:700;font-size:.82rem;vertical-align:middle;padding:10px 14px;border-right:2px solid #e8ebf4;}
    .gt td.hol-dc{background:#f0fdf4;color:#10c98e;font-weight:700;font-size:.82rem;vertical-align:middle;padding:10px 14px;border-right:2px solid #e8ebf4;}
    .gt td.lc{background:#fffbeb;text-align:center;vertical-align:middle;}
    .gt td.ec{text-align:center;color:#e0e4f0;font-size:.8rem;vertical-align:middle;}
    .gt td.off-cell{background:#fef9f9;vertical-align:middle;text-align:center;}
    .gt td.sun-cell{background:#f8f9fc;vertical-align:middle;text-align:center;}
    .gt td.hol-cell{background:#f0fdf4;vertical-align:top;padding:6px 8px;}
    .ge{border-radius:8px;padding:6px 8px;margin-bottom:3px;}
    .ge:last-child{margin-bottom:0;}
    .ge-s{font-size:.76rem;font-weight:700;line-height:1.2;}
    .ge-t{font-size:.66rem;margin-top:2px;}
    .ge-tm{font-size:.62rem;font-family:'JetBrains Mono',monospace;margin-top:2px;}
    .hol-chip{display:flex;align-items:center;gap:5px;padding:5px 8px;border-radius:8px;background:#dcfce7;border-left:3px solid #10c98e;margin-bottom:3px;}
    .lt{width:100%;border-collapse:collapse;}
    .lt thead{background:#fafbff;border-bottom:1px solid #edf0f9;}
    .lt th{padding:10px 16px;text-align:center !important;font-size:.65rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
    .lt tbody tr{border-bottom:1px solid #f3f5fb;}
    .lt tbody tr:last-child{border-bottom:none;}
    .lt tbody tr:hover{background:#fafbff;}
    .lt td{ padding:11px 16px;font-size:.8rem;vertical-align:middle; text-align:center !important; }
    .lt tr.off-row td{background:#fef9f9;}
    .lt tr.hol-row td{background:#f0fdf4;}
    .lt tr.sun-row td{background:#f8f9fc;}
    .pbadge{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:8px;background:#fff7ed;color:#f97316;font-size:.76rem;font-weight:700;font-family:'JetBrains Mono',monospace;}
    .dbadge{display:inline-flex;font-size:.72rem;font-weight:600;padding:3px 9px;border-radius:99px;}
    .tcell{font-size:.74rem;font-family:'JetBrains Mono',monospace;color:#6b7194;white-space:nowrap;}
    .tt-cards{display:none;padding:10px;}
    .tt-card{background:white;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
    .tt-card.off-card{background:#fef9f9;}
    .tt-card.sun-card{background:#f8f9fc;}
    .tt-card.hol-card{background:#f0fdf4;}
    .tt-card-day{font-weight:700;font-size:.86rem;color:#0f1123;margin-bottom:8px;display:flex;align-items:center;justify-content:space-between;gap:8px;}
    .tt-card-period{display:flex;align-items:center;gap:10px;padding:6px 0;border-bottom:1px solid #f0f2f8;}
    .tt-card-period:last-child{border-bottom:none;}
    @media(max-width:768px){
      .gwrap{display:none;}
      .lt-wrap{display:none !important;}
      .tt-cards{display:block;}
    }
  `;

  return (
    <div className="pp"><style>{sharedStyle+ttExtraStyle}</style>
      <div className="pp-hdr">
        <div><div className="pp-title">Timetable — {activeChild.name}</div><div className="pp-sub">{data?.className} — Section {data?.section}</div></div>
        <div className="vtoggle">
          <button className={`vbtn${viewMode==='list'?' on':''}`} onClick={()=>setViewMode('list')}><ListBulletIcon/>List</button>
          <button className={`vbtn${viewMode==='grid'?' on':''}`} onClick={()=>setViewMode('grid')}><TableCellsIcon/>Grid</button>
        </div>
      </div>

      {!entries.length ? <div className="pp-empty">No timetable set yet.</div> : (
        <>
          {viewMode==='list'&&(
            <div className="dtabs">
              <button className={`dtab${activeDay==='All'?' on':''}`} onClick={()=>setActiveDay('All')}>All</button>
              {ALL_DAYS.map(d=>{ const type=getDayType(d); const cls2=type==='off'||type==='sunday'?' off-tab':type==='holiday'?' hol-tab':''; return <button key={d} className={`dtab${cls2}${activeDay===d?' on':''}`} onClick={()=>setActiveDay(d)}>{d.slice(0,3)}{type==='off'||type==='sunday'?' 📅':type==='holiday'?' 🎉':''}</button>; })}
            </div>
          )}
          <div className="panel">
            <div className="panel-head"><div className="panel-title">{viewMode==='list'&&activeDay!=='All'?activeDay:'Weekly Schedule'}</div></div>

            {viewMode==='list'&&(
              <div className="lt-wrap" style={{overflowX:'auto'}}>
                <table className="lt">
                  <thead><tr><th>Period</th>{activeDay==='All'&&<th>Day</th>}<th>Time</th><th>Subject</th><th>Teacher</th></tr></thead>
                  <tbody>
                    {listDays.map(day=>{
                      const type=getDayType(day); const dayE=entries.filter(e=>e.day===day).sort((a,b)=>(a.period||0)-(b.period||0)); const dayH=holidaysByDay[day]||[];
                      const holRows=dayH.map((h,hi)=><tr key={`hol-${day}-${hi}`} className="hol-row"><td colSpan={activeDay==='All'?5:4}><div style={{display:'flex',alignItems:'center',gap:8}}><span>🎉</span><span style={{fontWeight:700,color:'#065f46',fontSize:'.82rem'}}>{h.name}</span><span style={{fontSize:'.7rem',color:'#10c98e',fontFamily:"'JetBrains Mono',monospace"}}>{fmtDateShort(h.date)}</span></div></td></tr>);
                      if(type==='sunday'&&!dayE.length&&!dayH.length) return <tr key={`sun-${day}`} className="sun-row"><td><span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span></td>{activeDay==='All'&&<td><span className="dbadge" style={{background:'#f0f2f7',color:'#9094a8'}}>{day}</span></td>}<td colSpan={3}><span style={{fontSize:'.74rem',fontWeight:600,color:'#9094a8'}}>📅 Sunday — Weekly Off</span></td></tr>;
                      if(type==='off'&&!dayE.length&&!dayH.length) return <tr key={`off-${day}`} className="off-row"><td><span style={{color:'#fecaca',fontSize:'.72rem'}}>—</span></td>{activeDay==='All'&&<td><span className="dbadge" style={{background:'#fef2f2',color:'#ef4444'}}>{day}</span></td>}<td colSpan={3}><span style={{fontSize:'.74rem',fontWeight:600,color:'#ef4444'}}>📅 Weekly Off{satNote(day)&&<span style={{fontSize:'.68rem',color:'#f59e0b',marginLeft:6}}>({satNote(day)})</span>}</span></td></tr>;
                      if(!dayE.length&&dayH.length) return <>{holRows}</>;
                      return <>{holRows}{dayE.map((e,i)=>{ const color=subjColor(e.subject,uniqSubjs); return <tr key={`${day}-${i}`}><td><span className="pbadge">{e.period}</span></td>{activeDay==='All'&&<td><span className="dbadge" style={{background:'#fff7ed',color:'#f97316'}}>{day}</span></td>}<td><span className="tcell">{e.startTime&&e.endTime?`${e.startTime} – ${e.endTime}`:e.startTime||e.endTime||'—'}</span></td><td><span style={{display:'inline-flex',fontSize:'.76rem',fontWeight:600,padding:'4px 10px',borderRadius:99,background:`${color}18`,color}}>{e.subject}</span></td><td style={{fontSize:'.78rem',color:'#4a5075'}}>{e.teacher||'—'}</td></tr>; })}</>;
                    })}
                    {lunchStart&&<tr style={{background:'#fffbeb'}}><td colSpan={activeDay==='All'?5:4} style={{padding:'8px 16px',fontSize:'.76rem',fontWeight:600,color:'#92400e'}}>🍽 Lunch Break{lunchStart?` — ${lunchStart}${lunchEnd?` – ${lunchEnd}`:''}`:''}</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {viewMode==='grid'&&(
              <div className="gwrap">
                <table className="gt">
                  <thead><tr>
                    <th className="dh">Day</th>
                    {cols.map((col,ci)=>col.t==='l'?<th key={`lh-${ci}`} className="lh">🍽 Lunch{lunchStart&&<div style={{fontSize:'.6rem',fontWeight:400,fontFamily:"'JetBrains Mono',monospace",marginTop:2}}>{lunchStart}{lunchEnd?` – ${lunchEnd}`:''}</div>}</th>:<th key={`ph-${col.p}`}><div>P{col.p}</div>{periodTime(col.p)&&<div style={{fontSize:'.6rem',fontWeight:400,fontFamily:"'JetBrains Mono',monospace",color:'#b0b5cc',marginTop:2}}>{periodTime(col.p)}</div>}</th>)}
                  </tr></thead>
                  <tbody>
                    {ALL_DAYS.map(day=>{
                      const type=getDayType(day); const dayHols=holidaysByDay[day]||[]; const off=type==='off'||type==='sunday';
                      const dcClass=type==='sunday'?'sun-dc':type==='off'?'off-dc':type==='holiday'?'hol-dc':'dc';
                      return (
                        <tr key={day}>
                          <td className={`dc ${dcClass}`}>
                            <div>{day}</div>
                            {type==='off'&&<div style={{fontSize:'.62rem',color:'#ef4444',fontWeight:600,marginTop:2}}>📅 Off</div>}
                            {type==='sunday'&&<div style={{fontSize:'.62rem',color:'#9094a8',marginTop:2}}>📅 Off</div>}
                            {type==='holiday'&&<div style={{fontSize:'.62rem',color:'#10c98e',fontWeight:600,marginTop:2}}>🎉 Holiday</div>}
                            {satNote(day)&&<div style={{fontSize:'.58rem',color:'#f59e0b',fontWeight:600,marginTop:1}}>{satNote(day)}</div>}
                          </td>
                          {off?<td colSpan={cols.length||1} className={type==='sunday'?'sun-cell':'off-cell'}><div style={{fontSize:'.76rem',fontWeight:600,color:type==='sunday'?'#9094a8':'#ef4444'}}>{type==='sunday'?'📅 Sunday — Weekly Off':'📅 Weekly Off'}{satNote(day)&&<span style={{fontSize:'.68rem',color:'#f59e0b',marginLeft:6}}>({satNote(day)})</span>}</div>{dayHols.map((h,hi)=><div key={hi} className="hol-chip" style={{marginTop:6}}><span style={{fontSize:'.72rem',fontWeight:700,color:'#065f46'}}>🎉 {h.name}</span><span style={{fontSize:'.62rem',color:'#10c98e',marginLeft:4,fontFamily:"'JetBrains Mono',monospace"}}>{fmtDateShort(h.date)}</span></div>)}</td>
                          :cols.map((col,ci)=>{
                            if(col.t==='l') return <td key={`lc-${ci}-${day}`} className="lc"><span style={{fontSize:'.8rem'}}>🍽</span></td>;
                            const cellE=gridMap[col.p]?.[day]||[];
                            if(!cellE.length){ if(col.p===allPeriods[0]&&dayHols.length) return <td key={`hc-${col.p}-${day}`} className="hol-cell">{dayHols.map((h,hi)=><div key={hi} className="hol-chip"><span style={{fontSize:'.72rem',fontWeight:700,color:'#065f46'}}>🎉 {h.name}</span><span style={{fontSize:'.62rem',color:'#10c98e',marginLeft:4,fontFamily:"'JetBrains Mono',monospace"}}>{fmtDateShort(h.date)}</span></div>)}</td>; return <td key={`ec-${col.p}-${day}`} className={dayHols.length?'hol-cell':'ec'} style={{color:'#d1d5db',fontSize:'.72rem',textAlign:'center',verticalAlign:'middle'}}>—</td>; }
                            return <td key={`tc-${col.p}-${day}`}>{cellE.map((e,ei)=>{ const c=subjColor(e.subject,uniqSubjs); return <div key={ei} className="ge" style={{background:`${c}18`,borderLeft:`3px solid ${c}`,marginBottom:ei<cellE.length-1?3:0}}><div className="ge-s" style={{color:c}}>{e.subject}</div><div className="ge-t" style={{color:`${c}cc`}}>{e.teacher||'—'}</div>{e.startTime&&<div className="ge-tm" style={{color:`${c}99`}}>{e.startTime}{e.endTime?` – ${e.endTime}`:''}</div>}</div>; })}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile card fallback — full week, shown regardless of list/grid toggle */}
            <div className="tt-cards">
              {ALL_DAYS.map(day=>{
                const type=getDayType(day); const dayE=entries.filter(e=>e.day===day).sort((a,b)=>(a.period||0)-(b.period||0)); const dayH=holidaysByDay[day]||[];
                const cardCls = type==='off'||type==='sunday' ? 'off-card' : type==='holiday' ? 'hol-card' : '';
                return (
                  <div key={day} className={`tt-card ${cardCls}`}>
                    <div className="tt-card-day">
                      <span>{day}{satNote(day)&&<span style={{fontSize:'.68rem',color:'#f59e0b',fontWeight:600,marginLeft:6}}>({satNote(day)})</span>}</span>
                      {(type==='off'||type==='sunday') && <span className="dbadge" style={{background:'#fef2f2',color:'#ef4444'}}>📅 Off</span>}
                      {type==='holiday' && <span className="dbadge" style={{background:'#ecfdf5',color:'#10c98e'}}>🎉 Holiday</span>}
                    </div>
                    {dayH.map((h,hi)=>(
                      <div key={hi} className="hol-chip"><span style={{fontSize:'.72rem',fontWeight:700,color:'#065f46'}}>🎉 {h.name}</span><span style={{fontSize:'.62rem',color:'#10c98e',marginLeft:4,fontFamily:"'JetBrains Mono',monospace"}}>{fmtDateShort(h.date)}</span></div>
                    ))}
                    {!dayE.length && !dayH.length && (
                      <div style={{fontSize:'.76rem',color: type==='sunday'?'#9094a8':type==='off'?'#ef4444':'#c0c5dc'}}>
                        {type==='sunday'?'📅 Sunday — Weekly Off':type==='off'?'📅 Weekly Off':'No periods scheduled'}
                      </div>
                    )}
                    {dayE.map((e,i)=>{
                      const color=subjColor(e.subject,uniqSubjs);
                      return (
                        <div key={i} className="tt-card-period">
                          <span className="pbadge">{e.period}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <span style={{display:'inline-flex',fontSize:'.76rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:`${color}18`,color}}>{e.subject}</span>
                            <div style={{fontSize:'.72rem',color:'#4a5075',marginTop:3}}>{e.teacher||'—'}</div>
                          </div>
                          <span className="tcell">{e.startTime&&e.endTime?`${e.startTime} – ${e.endTime}`:e.startTime||e.endTime||'—'}</span>
                        </div>
                      );
                    })}
                    {lunchStart && !!dayE.length && (
                      <div style={{marginTop:6,padding:'6px 10px',background:'#fffbeb',borderRadius:8,fontSize:'.72rem',fontWeight:600,color:'#92400e'}}>🍽 Lunch — {lunchStart}{lunchEnd?` – ${lunchEnd}`:''}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── DATESHEETS ───────────────────────────────────────────────────────────────
export const ParentDatesheets = () => {
  const { activeChild } = useContext(ChildContext);
  const [datesheets, setDatesheets] = useState([]);
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!activeChild) return;
    setLoading(true);
    api.get(`/parent/datesheets/${activeChild._id}`)
      .then(r=>setDatesheets(r.data||[])).catch(()=>toast.error('Failed'))
      .finally(()=>setLoading(false));
  }, [activeChild?._id]);

  if (!activeChild) return <NoChild/>;

  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr"><div><div className="pp-title">Datesheets — {activeChild.name}</div><div className="pp-sub">Exam timetables</div></div></div>
      {loading?<div className="pp-loading"><div className="spinner"/>Loading…</div>
      :!datesheets.length?<div className="pp-empty">No datesheets published yet.</div>
      :<div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
        {datesheets.map(d=>(
          <div key={d._id} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.3rem'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginBottom:10,flexWrap:'wrap'}}>
              <div><div style={{fontWeight:700,fontSize:'.9rem',color:'#0f1123'}}>{d.title}</div><div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>{d.academicYear}{d.class?.name&&` · ${d.class.name}`} · {fmtDate(d.createdAt)}</div></div>
              {d.fileUrl&&<a href={d.fileUrl} target="_blank" rel="noreferrer" className="btn btn-orange btn-sm" style={{textDecoration:'none'}}>View PDF</a>}
            </div>
            {d.type==='manual'&&d.entries?.length>0&&(
              <>
              <div className="pp-tbl-wrap" style={{overflowX:'auto'}}>
                <table className="pp-table">
                  <thead><tr><th>Subject</th><th>Date</th><th>Time</th><th>Duration</th></tr></thead>
                  <tbody>{d.entries.map((e,i)=>(
                    <tr key={i}><td style={{fontWeight:600}}>{e.subject}</td><td><span className="mono">{fmtDate(e.date)}</span></td><td><span className="mono">{e.time||'—'}</span></td><td><span className="mono">{e.duration||'—'}</span></td></tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="pp-cards">
                {d.entries.map((e,i)=>(
                  <div key={i} className="pp-card">
                    <div className="pp-card-top"><div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{e.subject}</div><span className="mono">{fmtDate(e.date)}</span></div>
                    <div className="pp-card-row"><span className="pp-card-label">Time</span><span className="mono">{e.time||'—'}</span></div>
                    <div className="pp-card-row"><span className="pp-card-label">Duration</span><span className="mono">{e.duration||'—'}</span></div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        ))}
      </div>}
    </div>
  );
};

// ─── QUIZZES (view child's results; attempt directly when parent-only) ────────
const qzExtraStyle = `
  .qz-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#6b7194; cursor:pointer; margin-bottom:1rem; padding:6px 0; background:none; border:none; }
  .qz-back:hover { color:#f97316; }
  .qz-subj-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; margin-bottom:1rem; }
  .qz-subj-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; cursor:pointer; transition:all .18s; }
  .qz-subj-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.09); }
  .qz-row { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:14px 18px; margin-bottom:10px; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
  .qz-chip { display:inline-flex; align-items:center; padding:5px 13px; border-radius:99px; font-family:'JetBrains Mono',monospace; font-size:.88rem; font-weight:700; white-space:nowrap; }
  .qz-form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .qz-option-btn { display:flex; align-items:center; gap:10px; width:100%; text-align:left; padding:11px 14px; border:1.5px solid #e8ebf4; border-radius:11px; background:white; margin-bottom:8px; cursor:pointer; font-family:'Sora',sans-serif; font-size:.84rem; color:#1a1d2e; transition:.12s; }
  .qz-option-btn.correct { border-color:#10c98e; background:#ecfdf5; }
  .qz-option-btn.wrong   { border-color:#ef4444; background:#fef2f2; }
  .qz-option-letter { width:24px; height:24px; border-radius:7px; background:#f0f2f7; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; flex-shrink:0; }
`;

export const ParentQuizzes = () => {
  const { activeChild } = useContext(ChildContext);
  const [view,        setView]        = useState('subjects'); // subjects | quizlist | attempt
  const [quizzes,     setQuizzes]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeSubj,  setActiveSubj]  = useState(null);

  const [activeQuiz,  setActiveQuiz]  = useState(null);
  const [answers,     setAnswers]     = useState({});
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [submitting,  setSubmitting]  = useState(false);

  const fetchQuizzes = () => {
    if (!activeChild) return;
    setLoading(true);
    api.get(`/parent/quizzes/${activeChild._id}`)
      .then(r => setQuizzes(r.data||[]))
      .catch(() => toast.error('Failed to load assessments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchQuizzes(); }, [activeChild]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!activeChild) return <NoChild/>;

  const grouped = quizzes.reduce((acc,q) => {
    const subj = q.subject || 'General';
    if (!acc[subj]) acc[subj] = [];
    acc[subj].push(q);
    return acc;
  }, {});
  const subjects = Object.keys(grouped);

  const startQuiz = async (quiz) => {
    setLoadingQuiz(true);
    try {
      const { data } = await api.get(`/parent/quizzes/${activeChild._id}/${quiz._id}`);
      setActiveQuiz(data); setAnswers({}); setView('attempt');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load assessment'); }
    finally { setLoadingQuiz(false); }
  };

  const handleAnswer = (qIndex, idx) => setAnswers(prev => ({ ...prev, [qIndex]: idx }));

  const handleSubmitQuiz = async () => {
    const unanswered = activeQuiz.questions.filter((q,i)=>answers[i]===undefined).length;
    if (unanswered>0 && !window.confirm(`${unanswered} unanswered. Submit anyway?`)) return;
    setSubmitting(true);
    try {
      const answersArr = activeQuiz.questions.map((q,i) => ({ selectedIndex: answers[i]!==undefined ? answers[i] : null }));
      const { data } = await api.post(`/parent/quizzes/${activeChild._id}/${activeQuiz._id}/submit`, { answers: answersArr });
      toast.success(`Submitted! Score: ${data.score}/${data.totalQuestions} (${data.percentage}%)`);
      setActiveQuiz(null); setAnswers({}); setView('quizlist');
      fetchQuizzes();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  // ── ATTEMPT VIEW (parent-only classes only) ───────────────────────────────
  if (view==='attempt' && activeQuiz) {
    const answeredCount = Object.keys(answers).length;
    return (
      <div className="pp"><style>{sharedStyle}{qzExtraStyle}</style>
        <button className="qz-back" onClick={()=>{ setView('quizlist'); setActiveQuiz(null); setAnswers({}); }}>← Back</button>
        <div className="pp-hdr"><div><div className="pp-title">{activeQuiz.title}</div><div className="pp-sub">{answeredCount}/{activeQuiz.questions.length} answered · {activeChild.name}</div></div>
          <button className="btn btn-orange" onClick={handleSubmitQuiz} disabled={submitting}>{submitting?'Submitting…':'Submit ✓'}</button>
        </div>
        {activeQuiz.questions.map((q,i) => (
          <div key={i} className="panel" style={{padding:'14px 18px',marginBottom:10}}>
            <div style={{fontWeight:700,fontSize:'.86rem',color:'#0f1123',marginBottom:10}}>{i+1}. {q.question}</div>
            {q.options?.map((opt,j) => (
              <button key={j} className={`qz-option-btn${answers[i]===j?' correct':''}`} style={answers[i]===j?{borderColor:'#f97316',background:'#fff7ed'}:{}} onClick={()=>handleAnswer(i,j)}>
                <span className="qz-option-letter">{String.fromCharCode(65+j)}</span>{opt}
              </button>
            ))}
          </div>
        ))}
        <button className="btn btn-orange" style={{width:'100%',justifyContent:'center'}} onClick={handleSubmitQuiz} disabled={submitting}>{submitting?'Submitting…':'Submit ✓'}</button>
      </div>
    );
  }

  // ── QUIZ LIST (per subject) ────────────────────────────────────────────────
  if (view==='quizlist' && activeSubj) {
    const list = grouped[activeSubj] || [];
    const canAttempt = activeChild.studentPortalEnabled === false;
    return (
      <div className="pp"><style>{sharedStyle}{qzExtraStyle}</style>
        <button className="qz-back" onClick={()=>setView('subjects')}>← {activeSubj}</button>
        <div className="pp-hdr"><div><div className="pp-title">{activeSubj} — {activeChild.name}</div><div className="pp-sub">{list.length} assessment{list.length!==1?'s':''}</div></div></div>
        {list.length===0 ? <div className="pp-empty">No assessments here yet.</div> : list.map(q => {
          const attempted = !!q.attempt;
          const pct = q.attempt?.percentage;
          return (
            <div key={q._id} className="qz-row">
              <div>
                <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{q.title}</div>
                <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>
                  {q.numQuestions||q.questions?.length||''} questions
                  {q.dueDate && ` · Due: ${fmtDate(q.dueDate)}`}
                </div>
              </div>
              {attempted
                ? <span className="qz-chip" style={{background:pctBg(pct),color:pctColor(pct)}}>{q.attempt.score}/{q.attempt.totalQuestions}</span>
                : canAttempt && q.status==='assigned'
                ? <button className="btn btn-orange" onClick={()=>startQuiz(q)} disabled={loadingQuiz}>{loadingQuiz?'Loading…':'Start →'}</button>
                : <span className="sr-badge" style={{background:'#f0f2f7',color:'#9094a8'}}>{q.status==='closed' ? 'Closed' : 'Not attempted yet'}</span>}
            </div>
          );
        })}
      </div>
    );
  }

  // ── SUBJECT LIST ───────────────────────────────────────────────────────────
  const SUBJ_COLORS = ['#f97316','#3b6ef8','#10c98e','#8b5cf6','#ef4444','#06b6d4','#ec4899'];
  return (
    <div className="pp"><style>{sharedStyle}{qzExtraStyle}</style>
      <div className="pp-hdr"><div><div className="pp-title">Online Assessments — {activeChild.name}</div><div className="pp-sub">{activeChild.studentPortalEnabled===false ? 'View and attempt your child’s assigned assessments' : 'View your child’s quiz results'}</div></div></div>
      {loading ? <div className="pp-empty">Loading…</div>
      : subjects.length===0 ? (
        <div className="pp-empty">
          <div style={{fontSize:'2rem',marginBottom:8}}>🧠</div>
          <div style={{fontWeight:700,color:'#0f1123',marginBottom:4}}>No assessments assigned yet</div>
        </div>
      ) : (
        <div className="qz-subj-grid">
          {subjects.map((s,i) => {
            const list = grouped[s];
            const attemptedCount = list.filter(q=>q.attempt).length;
            return (
              <div key={s} className="qz-subj-card" onClick={()=>{ setActiveSubj(s); setView('quizlist'); }}>
                <div className="sr-card-icon" style={{background:SUBJ_COLORS[i%SUBJ_COLORS.length]}}>{s.slice(0,2).toUpperCase()}</div>
                <div className="sr-card-name">{s}</div>
                <div className="sr-card-meta">{list.length} assessment{list.length!==1?'s':''} · {attemptedCount} attempted</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── FEEDBACK (view only) ─────────────────────────────────────────────────────
export const ParentFeedback = () => {
  const { activeChild } = useContext(ChildContext);
  if (!activeChild) return <NoChild/>;
  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr"><div><div className="pp-title">Evaluation — {activeChild.name}</div><div className="pp-sub">Teacher feedback forms</div></div></div>
      <div className="pp-empty">
        <div style={{fontSize:'2rem',marginBottom:8}}>⭐</div>
        <div style={{fontWeight:700,color:'#0f1123',marginBottom:4}}>Feedback forms</div>
        <div>Feedback is filled by students. Ask your child to complete any pending evaluations.</div>
      </div>
    </div>
  );
};

// ─── PROFILE ──────────────────────────────────────────────────────────────────
export const ParentProfile = () => {
  const { children } = useContext(ChildContext);
  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [pwForm,   setPwForm]   = useState({ currentPassword:'', newPassword:'', confirm:'' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    api.get('/parent/profile').then(r=>setProfile(r.data)).catch(()=>toast.error('Failed')).finally(()=>setLoading(false));
  }, []);

  const handleChangePw = async () => {
    if (!pwForm.currentPassword||!pwForm.newPassword) return toast.error('Fill all fields');
    if (pwForm.newPassword!==pwForm.confirm) return toast.error('Passwords do not match');
    try { setPwSaving(true); await api.put('/parent/profile/password',{currentPassword:pwForm.currentPassword,newPassword:pwForm.newPassword}); toast.success('Password changed'); setPwForm({currentPassword:'',newPassword:'',confirm:''}); }
    catch(e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setPwSaving(false); }
  };

  if (loading) return <div className="pp"><style>{sharedStyle}</style><div className="pp-loading"><div className="spinner"/>Loading…</div></div>;
  const initials=(n='')=>n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';

  return (
    <div className="pp"><style>{sharedStyle}</style>
      <div className="pp-hdr"><div><div className="pp-title">My Profile</div><div className="pp-sub">Parent account information</div></div></div>

      {/* Parent info */}
      <div style={{background:'linear-gradient(135deg,#f97316,#ef4444)',borderRadius:16,padding:'1.4rem 1.6rem',color:'white',marginBottom:'1.2rem',display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div style={{width:56,height:56,borderRadius:14,background:'rgba(255,255,255,.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',fontWeight:700,flexShrink:0}}>{initials(profile?.name)}</div>
        <div>
          <div style={{fontSize:'1.1rem',fontWeight:700}}>{profile?.name}</div>
          <div style={{fontSize:'.76rem',opacity:.8,marginTop:2}}>{profile?.email}</div>
          <div style={{fontSize:'.72rem',opacity:.7,marginTop:4,textTransform:'capitalize'}}>{profile?.relation||'Parent'}</div>
        </div>
      </div>

      {/* Children list */}
      {children.length > 0 && (
        <div className="panel" style={{marginBottom:'1rem'}}>
          <div className="panel-head"><div className="panel-title">My Children ({children.length})</div></div>
          {children.map((c,i)=>(
            <div key={c._id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 18px',borderBottom:i<children.length-1?'1px solid #f3f5fb':'none'}}>
              <div style={{width:36,height:36,borderRadius:10,background:'#fff7ed',color:'#f97316',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',flexShrink:0}}>{(c.name||'?')[0].toUpperCase()}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:'.84rem',color:'#0f1123'}}>{c.name}</div>
                <div style={{fontSize:'.72rem',color:'#9094a8'}}>{c.class} — Section {c.section} · Roll {c.rollNo} · {c.admissionNo}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Change password */}
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Change Password</div></div>
        <div style={{padding:'16px 18px',maxWidth:400}}>
          {[{label:'Current Password',key:'currentPassword'},{label:'New Password',key:'newPassword'},{label:'Confirm New Password',key:'confirm'}].map(f=>(
            <div key={f.key} className="form-field"><label className="form-label">{f.label}</label><PasswordInput className="form-input" value={pwForm[f.key]} onChange={e=>setPwForm({...pwForm,[f.key]:e.target.value})} /></div>
          ))}
          <button className="btn btn-orange" onClick={handleChangePw} disabled={pwSaving}>{pwSaving?'Changing…':'Change Password'}</button>
        </div>
      </div>
    </div>
  );
};